var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Container from '@mui/material/Container';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { ExpansionPanel, Tag as InternalTag, VerticalButton, Spotlight, VirtualizedTree, Tooltip } from '../';
import { withExpansionTree } from '../VirtualizedTree/withExpansionTree';
import { withSpotlight } from '../VirtualizedTree/withSpotlight';
import { getEvolvedDisableAccessor } from '../VirtualizedTree/utils';
import { useStyles } from '../ScopeList/styles';
import { useTheme } from '@mui/material';
import { getIsRtl } from '../utils';

var getCounter = function getCounter(items, disableAccessor, tagAccessor) {
  var selectedItemsIds = R.reduce(function (acc, item) {
    if (R.prop('isSelected', item)) {
      return R.append(item.id, acc);
    }
    return acc;
  }, [], items);
  var itemsIds = R.pluck('id', items);
  var count = R.pipe(R.uniq, R.length)(selectedItemsIds);
  var total = R.pipe(R.uniq, R.length)(itemsIds);
  if (R.is(Function, tagAccessor)) {
    return tagAccessor(count, total);
  }
  return count + '/' + total;
};
var getTotal = function getTotal(items, spotlightAcc) {
  var itemsIds = R.pluck('id', items);
  var total = R.pipe(R.uniq, R.length)(itemsIds);
  if (R.is(Function, spotlightAcc)) {
    return spotlightAcc(total);
  }
  return total;
};
var HierarchicalFilter = function HierarchicalFilter(props) {
  var id = props.id,
      items = props.items,
      changeSelection = props.changeSelection,
      onChangeActivePanel = props.onChangeActivePanel,
      activePanelId = props.activePanelId,
      Tag = props.Tag,
      testId = props.testId,
      _props$expansionPanel = props.expansionPanelProps,
      expansionPanelProps = _props$expansionPanel === undefined ? {} : _props$expansionPanel,
      _props$labelRenderer = props.labelRenderer,
      labelRenderer = _props$labelRenderer === undefined ? R.prop('label') : _props$labelRenderer,
      toggleBulk = props.toggleBulk,
      label = props.label,
      labels = props.labels,
      tagAccessor = props.tagAccessor,
      tagAriaLabel = props.tagAriaLabel,
      expandedIds = props.expandedIds,
      spotlight = props.spotlight,
      setSpotlight = props.setSpotlight,
      hasSpotlight = props.hasSpotlight,
      _props$disableAccesso = props.disableAccessor,
      disableAccessor = _props$disableAccesso === undefined ? R.always(false) : _props$disableAccesso,
      allItems = props.allItems,
      _props$simpleSelectio = props.simpleSelectionMode,
      simpleSelectionMode = _props$simpleSelectio === undefined ? false : _props$simpleSelectio;

  var classes = useStyles();
  var theme = useTheme();
  var isRtl = getIsRtl(theme);
  var Chip = R.isNil(Tag) ? InternalTag : Tag;
  var _allGroupedItems = R.groupBy(R.propOr('#ROOT', 'parentId'), allItems);
  var _disableAccessor = simpleSelectionMode ? disableAccessor : getEvolvedDisableAccessor(_allGroupedItems, disableAccessor, R.always(false));

  var tagValue = getCounter(items, _disableAccessor, tagAccessor);
  var tagValueLabel = getCounter(items, _disableAccessor, tagAriaLabel);
  var isDisabled = function isDisabled(item) {
    return R.is(Function, disableAccessor) ? disableAccessor(item) : false;
  };

  var onSelect = function onSelect(values) {
    changeSelection(id, values);
  };

  var onAdvancedToggle = function onAdvancedToggle() {
    toggleBulk({ expandedIds: expandedIds, spotlight: spotlight });
  };
  return React.createElement(
    ExpansionPanel,
    _extends({
      isOpen: R.equals(id, activePanelId),
      id: id,
      testId: '' + testId,
      label: labelRenderer({ id: id, label: label }),
      onChangeActivePanel: onChangeActivePanel,
      overflow: true,
      tagValueLabel: tagValueLabel,
      topElementComponent: React.createElement(
        Fragment,
        null,
        React.createElement(
          Container,
          { disableGutters: true, className: classes.spotlightContainer, role: 'presentation' },
          hasSpotlight && React.createElement(Spotlight, {
            className: { container: classes.middle },
            fullWidth: true,
            hasClearAll: true,
            placeholder: getTotal(items, R.prop('placeholder', labels)),
            term: spotlight.term,
            hasCommit: false,
            spotlight: { fields: {} },
            action: setSpotlight
          }),
          R.is(Function, toggleBulk) && hasSpotlight && React.createElement(
            Tooltip,
            {
              title: R.prop('iconLabel')(labels),
              key: id,
              placement: 'bottom',
              variant: 'light'
            },
            React.createElement(
              VerticalButton,
              {
                'aria-label': R.prop('iconLabel')(labels),
                classes: { root: classes.bulkSelectionIcon },
                onClick: onAdvancedToggle
              },
              React.createElement(ZoomOutMapIcon, { color: 'primary' })
            )
          )
        )
      ),
      tag: React.createElement(
        Chip,
        { items: items, tagValueLabel: tagValueLabel },
        tagValue
      )
    }, expansionPanelProps),
    React.createElement(VirtualizedTree, _extends({}, props, {
      items: items,
      changeSelection: onSelect,
      isRtl: isRtl,
      disableAccessor: _disableAccessor,
      isGreyed: isDisabled
    }))
  );
};

HierarchicalFilter.propTypes = process.env.NODE_ENV !== "production" ? {
  id: PropTypes.string,
  activePanelId: PropTypes.string,
  changeSelection: PropTypes.func,
  onChangeActivePanel: PropTypes.func,
  disableAccessor: PropTypes.func,
  HTMLRenderer: PropTypes.func,
  isRtl: PropTypes.bool,
  items: PropTypes.array,
  label: PropTypes.string,
  labels: PropTypes.shape({
    disableItemLabel: PropTypes.string,
    placeholder: PropTypes.func
  }),
  labelRenderer: PropTypes.func,
  Tag: PropTypes.func,
  testId: PropTypes.string,
  expansionPanelProps: PropTypes.object,
  toggleBulk: PropTypes.func,
  tagAccessor: PropTypes.func,
  expandedIds: PropTypes.object,
  spotlight: PropTypes.object,
  setSpotlight: PropTypes.func,
  hasSpotlight: PropTypes.bool,
  allItems: PropTypes.array,
  simpleSelectionMode: PropTypes.bool,
  tagAriaLabel: PropTypes.func
} : {};

export default withSpotlight(withExpansionTree(HierarchicalFilter));